import {
  DELETION_FLAG,
  ERROR_CODE,
  UniversalError,
  isFacilityKey,
  isOrganizationKey,
  isUserKey,
} from "@packages/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import { showError } from "#components";
import {
  useFacilityInfiniteQuery,
  useListedFacilityStaffs,
  useOperator,
  useUsername,
} from "#services";

export const Route = createFileRoute("/native-application")({
  component: Component,
});

function Component() {
  const navigate = useNavigate();

  const username = useUsername();
  const facilityInfiniteQuery = useFacilityInfiniteQuery(
    isOrganizationKey(username)
      ? {
          index: "organizationKey-deleted-gsi",
          condition: [
            ["organizationKey", "=", username],
            ["deleted", "=", DELETION_FLAG.DEFAULT],
          ],
          limit: 25,
        }
      : { limit: 25 },
    { enabled: isOrganizationKey(username) },
  );
  const { pages: facilityStaffPages, loading: facilityStaffLoading } = useListedFacilityStaffs(
    useMemo(
      function () {
        return isUserKey(username)
          ? { userKey: username, deleted: DELETION_FLAG.DEFAULT }
          : undefined;
      },
      [username],
    ),
    { enabled: isUserKey(username) },
  );
  const operator = useOperator(isUserKey(username) && username);

  const facilities = useMemo(
    function () {
      return (
        facilityInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.facilities;
        }) ?? []
      );
    },
    [facilityInfiniteQuery.data],
  );

  const facilityStaffs = useMemo(
    function () {
      return !facilityStaffLoading
        ? facilityStaffPages.flatMap(function (page) {
            return page.facilityStaffs;
          })
        : [];
    },
    [facilityStaffLoading, facilityStaffPages],
  );

  useEffect(
    function () {
      if (username === undefined) {
        console.debug("ユーザー名を取得中です。");
        return;
      } else if (isOrganizationKey(username) && facilityInfiniteQuery.isLoading) {
        console.debug("組織の施設一覧を取得中です。");
        return;
      } else if (isUserKey(username) && facilityStaffs === undefined) {
        console.debug("ユーザーの施設職員一覧を取得中です。");
        return;
      } else if (isUserKey(username) && operator === undefined) {
        console.debug("ユーザーの運営者を取得中です。");
        return;
      }

      if (!username) {
        console.debug("ログインしていないため、ログインページに遷移します。");
        navigate({ to: "/login" });
        return;
      } else if (isOrganizationKey(username)) {
        if (facilities.length > 0) {
          console.debug("組織であるため、施設入居者ページに遷移します。");
          navigate({
            params: { facilityKey: facilities[0].facilityKey },
            to: "/facilities/$facilityKey",
          });
          return;
        } else {
          showError(new UniversalError(ERROR_CODE.FACILITY_NOT_FOUND));
          return;
        }
      } else if (isFacilityKey(username)) {
        console.debug("施設であるため、施設入居者ページに遷移します。");
        navigate({
          to: "/facilities/$facilityKey",
          params: { facilityKey: username },
        });
        return;
      } else if (operator) {
        console.debug("運営者であるため、管理コンソールに遷移します。");
        navigate({ to: "/management/system-dashboard" });
      } else if (facilityStaffs.length > 0) {
        console.debug("施設職員であるため、施設入居者ページに遷移します。");
        navigate({
          params: { facilityKey: facilityStaffs[0].facilityKey },
          to: "/facilities/$facilityKey",
        });
        return;
      }
    },
    [facilityStaffs, facilities, facilityInfiniteQuery.isLoading, navigate, operator, username],
  );

  return null;
}
